import React, { useEffect, useMemo, useState } from "react";
import { listAuditLogs } from "../api/agentApi";
import { useUser } from "./UserContext";
import { DetailsCell } from "./DetailsCell";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { PERMISSIONS } from "../permissions";

const COPPER = BRAND_COLORS.primary.main;
const COPPER_DARK = BRAND_COLORS.primary.dark;
const COPPER_BORDER = BRAND_COLORS.primary.border;
const BORDER_SOFT = "#E5D4C4";
const MUTED = "#6F6A64";
const TABLE_HEADER_BG = "#F7F2ED";
const PAGE_SIZE = 20;

const formControlSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
  },
};

const secondaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  color: COPPER_DARK,
  textTransform: "none",
  fontWeight: 850,
  minHeight: 40,
  px: 2,
  "&:hover": {
    bgcolor: BRAND_COLORS.primary.softSubtle,
  },
};

const statusMeta = {
  success: {
    label: "Success",
    icon: <CheckCircleOutlineIcon fontSize="small" />,
    color: "#2F6F3E",
    bg: "rgba(47, 111, 62, 0.1)",
    border: "rgba(47, 111, 62, 0.22)",
  },
  failed: {
    label: "Failed",
    icon: <ErrorOutlineIcon fontSize="small" />,
    color: "#A33A2B",
    bg: "rgba(163, 58, 43, 0.1)",
    border: "rgba(163, 58, 43, 0.24)",
  },
  error: {
    label: "Error",
    icon: <ErrorOutlineIcon fontSize="small" />,
    color: "#A33A2B",
    bg: "rgba(163, 58, 43, 0.1)",
    border: "rgba(163, 58, 43, 0.24)",
  },
  started: {
    label: "Started",
    icon: <PlayCircleOutlineIcon fontSize="small" />,
    color: COPPER_DARK,
    bg: BRAND_COLORS.primary.soft,
    border: BRAND_COLORS.primary.border,
  },
};

function humanize(value = "") {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatTimestamp(timestamp) {
  if (!timestamp) return { primary: "-", secondary: "" };
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return { primary: timestamp, secondary: "" };
  }
  return {
    primary: date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    secondary: date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
}

function truncateMiddle(value = "", max = 24) {
  const text = String(value || "");
  if (!text || text.length <= max) return text || "-";
  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${text.slice(0, head)}...${text.slice(text.length - tail)}`;
}

function StatusPill({ status }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const key = String(status || "").toLowerCase();
  const meta = statusMeta[key] || {
    label: humanize(status || "Unknown"),
    icon: <EventNoteOutlinedIcon fontSize="small" />,
    color: "#475569",
    bg: "rgba(100, 116, 139, 0.1)",
    border: "rgba(100, 116, 139, 0.22)",
  };
  return (
    <Chip
      size="small"
      icon={meta.icon}
      label={meta.label}
      variant="outlined"
      sx={{
        bgcolor: meta.bg,
        borderColor: meta.border,
        color: isDark && key === "started" ? "#F2C69B" : meta.color,
        fontWeight: 700,
        borderRadius: UI_RADIUS.chip,
        height: 28,
        px: 0.5,
        "& .MuiChip-icon": { color: isDark && key === "started" ? "#F2C69B" : meta.color, fontSize: 16 },
      }}
    />
  );
}

function SummaryTile({ label, value, helper, tone = "neutral" }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent =
    tone === "success" ? (isDark ? "#91CD99" : "#2F6F3E") : tone === "failed" ? (isDark ? "#FF9C91" : "#A33A2B") : (isDark ? "#F2C69B" : COPPER_DARK);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${COPPER_BORDER}`,
        bgcolor: "background.paper",
        minHeight: 112,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 800, letterSpacing: 0.8 }}
      >
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: accent }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, color: "text.secondary" }}>
        {helper}
      </Typography>
    </Paper>
  );
}

function AuditLogsPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const panel = theme.palette.background.paper;
  const subtleCopper = isDark ? alpha(theme.palette.primary.main, 0.16) : "rgba(184, 115, 51, 0.06)";
  const copperText = isDark ? "#F2C69B" : COPPER_DARK;
  const { can } = useUser();
  const canReadAudit = Boolean(can?.(PERMISSIONS.AUDIT_READ));
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    username: "",
    action: "",
    status: "",
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters],
  );

  const pageSummary = useMemo(() => {
    const success = logs.filter((log) => String(log.status).toLowerCase() === "success").length;
    const failed = logs.filter((log) =>
      ["failed", "error"].includes(String(log.status).toLowerCase()),
    ).length;
    const actors = new Set(logs.map((log) => log.username).filter(Boolean)).size;
    return { success, failed, actors };
  }, [logs]);

  const fetchLogs = () => {
    if (!canReadAudit) return;
    setLoading(true);
    setError("");
    const activeFilters = {};
    ["username", "action", "status"].forEach((key) => {
      const value = filters[key]?.trim();
      if (value) {
        activeFilters[key] = `%${value}%`;
      }
    });
    listAuditLogs({ ...activeFilters, page, pageSize: PAGE_SIZE })
      .then((res) => {
        setLogs(res.logs || []);
        setTotal(res.total || 0);
      })
      .catch((err) => {
        setError(err.message || "Failed to load audit logs");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, canReadAudit]);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({ username: "", action: "", status: "" });
  };

  if (!canReadAudit) {
    return (
      <Box sx={{ pl: PAGE_SHELL.paddingLeft, pr: PAGE_SHELL.paddingRight, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Alert severity="error">Access denied. You do not have permission to read audit logs.</Alert>
      </Box>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstVisible = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastVisible = Math.min(page * PAGE_SIZE, total);

  return (
    <Box sx={{ pl: PAGE_SHELL.paddingLeft, pr: PAGE_SHELL.paddingRight, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Audit Logs
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35, color: MUTED }}>
            Review security, agent, and configuration activity across the workspace.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
            disabled={loading}
            sx={{
              ...secondaryActionButtonSx,
              borderColor: COPPER_BORDER,
              color: copperText,
              bgcolor: subtleCopper,
              "&:hover": {
                bgcolor: isDark ? alpha(theme.palette.primary.main, 0.24) : BRAND_COLORS.primary.softHover,
                borderColor: COPPER_BORDER,
              },
            }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: PAGE_SHELL.sectionGap,
          mb: PAGE_SHELL.sectionGap,
        }}
      >
        <SummaryTile label="Total Events" value={total} helper="Matching current filters" />
        <SummaryTile label="Visible Success" value={pageSummary.success} helper="On this page" tone="success" />
        <SummaryTile label="Visible Issues" value={pageSummary.failed} helper="Failed or error events" tone="failed" />
        <SummaryTile label="Visible Actors" value={pageSummary.actors} helper="Unique users on this page" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: PAGE_SHELL.sectionGap,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${COPPER_BORDER}`,
          bgcolor: panel,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", lg: "center" }}
        >
          <Chip
            icon={<FilterListIcon />}
            label={activeFilterCount ? `${activeFilterCount} filters` : "Filters"}
            variant="outlined"
            sx={{
              alignSelf: { xs: "flex-start", lg: "center" },
              borderColor: COPPER_BORDER,
              color: copperText,
              fontWeight: 700,
              bgcolor: subtleCopper,
              borderRadius: UI_RADIUS.chip,
              "& .MuiChip-icon": { color: copperText },
            }}
          />
          <TextField
            label="Username"
            size="small"
            value={filters.username}
            onChange={(event) => updateFilter("username", event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: "100%", lg: 220 }, flex: 1, ...formControlSx }}
          />
          <TextField
            label="Action"
            size="small"
            value={filters.action}
            onChange={(event) => updateFilter("action", event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: "100%", lg: 240 }, flex: 1, ...formControlSx }}
          />
          <Select
            size="small"
            value={filters.status}
            displayEmpty
            onChange={(event) => updateFilter("status", event.target.value)}
            sx={{
              minWidth: { xs: "100%", lg: 180 },
              bgcolor: isDark ? alpha("#FFF7EF", 0.08) : BRAND_COLORS.primary.softSubtle,
              borderRadius: UI_RADIUS.input,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: COPPER_BORDER },
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="started">Started</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
          <Button
            variant="text"
            onClick={clearFilters}
            disabled={!activeFilterCount}
            sx={{ ...secondaryActionButtonSx, color: copperText, alignSelf: { xs: "flex-start", lg: "center" } }}
          >
            Clear
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${BORDER_SOFT}`,
          overflow: "hidden",
          bgcolor: panel,
        }}
      >
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress size={28} sx={{ color: COPPER }} />
            <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>
              Loading audit trail
            </Typography>
          </Box>
        ) : (
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: TABLE_HEADER_BG,
                  "& th": {
                    py: 1.5,
                    color: isDark ? "#F2C69B" : MUTED,
                    fontSize: "0.76rem",
                    fontWeight: 800,
                    letterSpacing: 0.7,
                    textTransform: "uppercase",
                    borderBottom: `1px solid ${COPPER_BORDER}`,
                  },
                }}
              >
                <TableCell sx={{ width: "15%" }}>Time</TableCell>
                <TableCell sx={{ width: "13%" }}>Actor</TableCell>
                <TableCell sx={{ width: "15%" }}>Action</TableCell>
                <TableCell sx={{ width: "12%" }}>Status</TableCell>
                <TableCell sx={{ width: "18%" }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box component="span">Target</Box>
                    <Tooltip title="The affected object for this audit event, such as a user, agent, workflow, run, or system resource ID.">
                      <HelpOutlineIcon
                        aria-label="Target column help"
                        sx={{ fontSize: 15, color: "text.secondary", cursor: "help" }}
                      />
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 5, textAlign: "center" }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        No audit events found
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        Adjust the filters or refresh to check for new activity.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const time = formatTimestamp(log.timestamp);
                  return (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{
                        "& td": {
                          py: 1.35,
                          borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
                          verticalAlign: "top",
                        },
                        "&:hover": { bgcolor: isDark ? alpha(theme.palette.primary.main, 0.10) : BRAND_COLORS.primary.softSubtle },
                      }}
                    >
                      <TableCell>
                        <Tooltip title={log.timestamp || ""}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                              {time.primary}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {time.secondary}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {log.username || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {humanize(log.action)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusPill status={log.status} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={log.target_id || ""}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                              fontSize: "0.78rem",
                              color: "text.secondary",
                              wordBreak: "break-word",
                            }}
                          >
                            {truncateMiddle(log.target_id, 28)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <DetailsCell details={log.details} />
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mt: PAGE_SHELL.sectionGap }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Showing {firstVisible}-{lastVisible} of {total} events
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            disabled={page === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label="Previous page"
            sx={{ border: `1px solid ${COPPER_BORDER}`, borderRadius: UI_RADIUS.iconButton, color: COPPER_DARK }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary", minWidth: 92, textAlign: "center" }}>
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
            aria-label="Next page"
            sx={{ border: `1px solid ${COPPER_BORDER}`, borderRadius: UI_RADIUS.iconButton, color: COPPER_DARK }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}

export default AuditLogsPage;
